import { Badge, Button, Center, Container, Group, Loader, Stack, Text } from '@mantine/core';
import { useEffect } from 'react';
import { AppTitle } from '../components/AppTitle.js';
import {
  ClosedNotice,
  ConnectionBadge,
  ErrorBanner,
  RejectedNotice,
} from '../components/ConnectionBanner.js';
import { ptWithUnit, streetLabel } from '../format.js';
import { navigate } from '../routing.js';
import { useStore } from '../store.js';
import { getToken } from '../token.js';
import { ActionLog } from './ActionLog.js';
import classes from './DisplayApp.module.css';
import { JoinPanel } from './JoinPanel.js';
import { OperatorPanel } from './OperatorPanel.js';
import { SeatRing } from './SeatRing.js';
import { ShowdownPanel } from './ShowdownPanel.js';

/**
 * 中央ディスプレイ（要件11）。
 *
 * 特定プレイヤーに紐づかない。1ルームに複数接続できる（表示のみのため排他制御しない、
 * 要件1.8）。
 *
 * 画面は表示領域と同じ高さに固定し、スクロールさせない（`DisplayApp.module.css`）。
 * 卓から離れて目視する用途では、スクロール位置によって見える内容が変わると使えない。
 * 縦に伸びうる要素（履歴、勝者選択）は上限を持ち、余った高さを卓が使う。
 */
export function DisplayApp({ roomCode }: { roomCode: string }): React.ReactElement {
  const connect = useStore((s) => s.connect);
  const view = useStore((s) => s.view);
  const status = useStore((s) => s.status);
  const error = useStore((s) => s.error);
  const clearError = useStore((s) => s.clearError);

  useEffect(() => {
    // 中央ディスプレイも匿名トークンで接続する。席には紐づかない。
    connect({ roomCode, token: getToken(), role: 'DISPLAY' });
  }, [connect, roomCode]);

  if (status === 'roomClosed' || status === 'rejected') {
    return (
      <Container size="sm" py="xl">
        <Stack>
          {status === 'roomClosed' ? <ClosedNotice /> : <RejectedNotice roomCode={roomCode} />}
          <Button onClick={() => navigate('/')}>入口へ戻る</Button>
        </Stack>
      </Container>
    );
  }

  if (view === null) {
    return (
      <Center h="100vh">
        <Stack align="center">
          <Loader />
          <Text c="dimmed">卓の状態を読み込み中…</Text>
        </Stack>
      </Center>
    );
  }

  const hand = view.hand;
  const hasEmptySeat = view.seats.some((s) => !s.occupied);

  return (
    <div className={classes.shell}>
      <Container size={2000} pt="md" px="md" w="100%">
        <Stack gap="md">
          <Group justify="space-between" wrap="wrap">
            <Group align="baseline" gap="lg">
              <AppTitle order={2} />
              <Text size="lg" fw={600}>
                {ptWithUnit(view.config.smallBlind)} / {ptWithUnit(view.config.bigBlind)}
              </Text>
              {/* 現在のストリートを表示する（要件11.7、11.10）。 */}
              <Badge
                size="xl"
                variant="light"
                color={hand === null ? 'gray' : 'teal'}
                data-testid="street"
                data-street={hand === null ? 'NONE' : hand.street}
              >
                {hand === null ? 'ハンド待機' : streetLabel(hand.street)}
              </Badge>
              {view.handNo > 0 && (
                <Text size="sm" c="dimmed">
                  ハンド #{view.handNo}
                </Text>
              )}
            </Group>
            <Group>
              <Text size="sm" c="dimmed">
                ルーム {view.code}
              </Text>
              <ConnectionBadge status={status} />
            </Group>
          </Group>

          <ErrorBanner error={error} onClose={clearError} />
        </Stack>
      </Container>

      {/*
        余った高さはここが受け持つ。勝者選択が出ると卓が縮む。画面の向きで配置を変える
        （DisplayApp.module.css）。
      */}
      <Container size={2000} px="md" py="md" w="100%" className={classes.main}>
        <div className={classes.table}>
          <SeatRing view={view} />
        </div>
        {hasEmptySeat && (
          <div className={classes.join}>
            <JoinPanel roomCode={view.code} />
          </div>
        )}
        <div className={classes.log}>
          <ActionLog log={view.log} />
        </div>
      </Container>

      <Container size={2000} pb="md" px="md" w="100%">
        <OperatorPanel view={view} />
      </Container>

      {/*
        勝者選択は最下段。フローに置くので運営操作を覆わない。
      */}
      <ShowdownPanel view={view} />
    </div>
  );
}
